import {
    ApplicationConfig,
    EnvironmentProviders,
    ErrorHandler,
    inject,
    LOCALE_ID,
    Provider,
    provideAppInitializer,
} from '@angular/core';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import LaraLightBlue from '@primeng/themes/lara';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import * as Sentry from '@sentry/angular';
import { provideTranslateService } from '@ngx-translate/core';
import { APP_ROUTES } from './app.routes';
import { authInterceptor } from './core/interceptors/auth-management.interceptor';
import { partnerCodeInterceptor } from './core/interceptors/partner-code.interceptor';
import { AuthService } from './features/auth/services/auth.service';
import { PartnerConfigService } from './core/services/partner-config.service';
import { environment } from '../environments/environment';

const angularMaterialDatePickerFormat = {
    parse: { dateInput: 'DD/MM/YYYY' },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

function appInitializer(authService: AuthService) {
    return () =>
        new Promise<void>((resolve) => {
            authService.getUserByToken().subscribe().add(resolve);
        });
}

const preset = definePreset(LaraLightBlue, {
    semantic: {
        primary: {
            50: '#f0f6ff',
            100: '#d9e9ff',
            200: '#b3d3ff',
            300: '#80b4ff',
            400: '#4d8fff',
            500: 'var(--klaro-yale-blue)',
            600: '#13447d',
            700: '#0e3462',
            800: '#0a2547',
            900: '#06182e',
            950: '#030e1c',
        },
    },
    components: {
        datepicker: {
            date: {
                selectedBackground: 'var(--klaro-alice-blue)',
                selectedColor: 'var(--klaro-yale-blue)',
            },
        },
        toggleswitch: {
            root: {
                disabledBackground: 'var(--klaro-cultured) !important',
            },
            handle: {
                disabledBackground: 'var(--klaro-dark-gray) !important',
            },
        },
    },
});

const sentryProviders: (Provider | EnvironmentProviders)[] = [];
if (environment.host === 'prod' || environment.host === 'staging') {
    sentryProviders.push(
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) },
        { provide: Sentry.TraceService, deps: [Router] },
        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        }),
    );
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(APP_ROUTES, withComponentInputBinding()),
        provideHttpClient(withInterceptors([partnerCodeInterceptor, authInterceptor])),
        provideAnimations(),
        ...sentryProviders,
        { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
        provideMomentDateAdapter(undefined, { useUtc: false }),
        { provide: LOCALE_ID, useValue: 'it-IT' },
        { provide: MAT_DATE_FORMATS, useValue: angularMaterialDatePickerFormat },
        provideAppInitializer(async () => {
            const partnerConfigService = inject(PartnerConfigService);
            const authService = inject(AuthService);
            await partnerConfigService.loadConfig();
            await appInitializer(authService)();
        }),
        MessageService,
        provideTranslateService(),
        providePrimeNG({
            theme: {
                preset: preset,
                options: {
                    darkModeSelector: false,
                },
            },
        }),
    ],
};
